import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Zap, TrendingUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  reason: 'pets' | 'storage' | 'premium-feature';
  currentTier: string;
  customMessage?: string;
}

const UpgradeModal = ({ isOpen, onClose, reason, currentTier, customMessage }: UpgradeModalProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleUpgrade = () => {
    onClose();
    navigate('/subscription');
  };

  const getContent = () => {
    switch (reason) {
      case 'pets':
        return {
          icon: <Sparkles className="h-12 w-12 text-primary mx-auto mb-4" />,
          title: t('upgrade.pets.title', 'Add More Pets'),
          description: customMessage || t('upgrade.pets.description', 'You\'ve reached the pet limit for your {{tier}} plan. Upgrade to add more furry friends!', { tier: currentTier }),
          benefits: [
            t('upgrade.pets.benefit1', 'Add up to 5 pets'),
            t('upgrade.pets.benefit2', 'More storage for photos & videos'),
            t('upgrade.pets.benefit3', 'Premium features unlocked')
          ]
        };
      case 'storage':
        return {
          icon: <TrendingUp className="h-12 w-12 text-primary mx-auto mb-4" />,
          title: t('upgrade.storage.title', 'Storage Full'),
          description: customMessage || t('upgrade.storage.description', 'Your storage is full! Upgrade to get more space for your precious memories.'),
          benefits: [
            t('upgrade.storage.benefit1', 'Up to 2GB of storage'),
            t('upgrade.storage.benefit2', 'Upload HD photos & videos'),
            t('upgrade.storage.benefit3', 'Never worry about space again')
          ]
        };
      case 'premium-feature':
        return {
          icon: <Zap className="h-12 w-12 text-primary mx-auto mb-4" />,
          title: t('upgrade.feature.title', 'Premium Feature'),
          description: customMessage || t('upgrade.feature.description', 'This feature is available on paid plans. Unlock it now!'),
          benefits: [
            t('upgrade.feature.benefit1', 'All premium features'),
            t('upgrade.feature.benefit2', 'Priority support'),
            t('upgrade.feature.benefit3', 'Advanced AI tools')
          ]
        };
      default:
        return {
          icon: <Sparkles className="h-12 w-12 text-primary mx-auto mb-4" />,
          title: t('upgrade.default.title', 'Upgrade Your Plan'),
          description: customMessage || t('upgrade.default.description', 'Get more features with a paid plan'),
          benefits: []
        };
    }
  };

  const content = getContent();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          {content.icon}
          <DialogTitle className="text-center text-2xl">{content.title}</DialogTitle>
          <DialogDescription className="text-center text-base pt-2">
            {content.description}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {content.benefits.length > 0 && (
            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <p className="font-semibold text-sm">{t('upgrade.benefits', 'What you get:')}</p>
              <ul className="space-y-1">
                {content.benefits.map((benefit, index) => (
                  <li key={index} className="flex items-center gap-2 text-sm">
                    <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-col gap-2">
            <Button 
              onClick={handleUpgrade} 
              size="lg"
              className="w-full"
            >
              {t('upgrade.viewPlans', 'View Upgrade Plans')}
            </Button>
            <Button 
              onClick={onClose} 
              variant="ghost"
              size="lg"
              className="w-full"
            >
              {t('common.cancel', 'Maybe Later')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UpgradeModal;
